import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../ui/Modal/Modal';
import { Button } from '../../ui/Button/Button';
import { Input } from '../../ui/Input/Input';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, XIcon } from '../../Icons/Icons';
import { useEvaluationStore } from '../../../store/evaluationStore';
import { encryptProductTitle, decryptProductTitle } from '../../../utils/encryption';
import { useUserSettingsStore } from '../../../store/userSettingsStore';
import './EditProductModal.css';

interface EditProductModalProps {
  isOpen: boolean;
  productIndex: number;
  onClose: () => void;
  onNavigate: (direction: 'prev' | 'next') => void;
}

export const EditProductModal: React.FC<EditProductModalProps> = ({
  isOpen,
  productIndex,
  onClose,
  onNavigate
}) => {
  const { t } = useTranslation();
  const { products, updateProduct } = useEvaluationStore();
  const { userSettings } = useUserSettingsStore();
  const securityModeEnabled = userSettings?.securityMode || false;
  
  const [editTitle, setEditTitle] = React.useState('');
  const [editComment, setEditComment] = React.useState('');
  const [editLink, setEditLink] = React.useState('');
  const [decryptedTitle, setDecryptedTitle] = React.useState('');
  
  const product = products[productIndex];
  const canNavigatePrev = productIndex > 0;
  const canNavigateNext = productIndex < products.length - 1;
  
  // キーボードイベントのリスナー
  React.useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && canNavigatePrev && (!e.target || !(e.target as HTMLElement).matches('input, textarea'))) {
        e.preventDefault();
        handleNavigate('prev');
      } else if (e.key === 'ArrowRight' && canNavigateNext && (!e.target || !(e.target as HTMLElement).matches('input, textarea'))) {
        e.preventDefault();
        handleNavigate('next');
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [productIndex, canNavigatePrev, canNavigateNext, onClose, onNavigate]);
  
  // 現在の製品データを読み込む
  React.useEffect(() => {
    if (!product) return;
    
    // タイトルの復号化
    const decryptTitle = async () => {
      if (securityModeEnabled && userSettings?.privateKey) {
        try {
          const decrypted = await decryptProductTitle(product.reviewTitle, userSettings.privateKey);
          setDecryptedTitle(decrypted);
          setEditTitle(decrypted);
        } catch (error) {
          console.error('復号化エラー:', error);
          setDecryptedTitle(product.reviewTitle);
          setEditTitle(product.reviewTitle);
        }
      } else {
        setDecryptedTitle(product.reviewTitle);
        setEditTitle(product.reviewTitle);
      }
    };
    
    decryptTitle();
    setEditComment(product.reviewComment || '');
    setEditLink(product.reviewLink || '');
  }, [product, productIndex, securityModeEnabled, userSettings?.privateKey]);
  
  const handleSave = async () => {
    if (!editTitle.trim()) return;
    
    let titleToStore = editTitle.trim();
    
    // セキュリティモードが有効な場合は暗号化
    if (securityModeEnabled && userSettings?.publicKey) {
      try {
        titleToStore = await encryptProductTitle(editTitle.trim(), userSettings.publicKey);
      } catch (error) {
        console.error(t('productRegistration.error.encryptionLog'), error);
        alert(t('productRegistration.error.encryptionFailed'));
        return;
      }
    }
    
    updateProduct(productIndex, {
      reviewTitle: titleToStore,
      reviewComment: editComment.trim() || '',
      reviewLink: editLink.trim() || undefined
    });
    
    onClose();
  };
  
  const handleNavigate = (direction: 'prev' | 'next') => {
    // 現在の変更を保存
    handleSave();
    // ナビゲート
    onNavigate(direction);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && canNavigatePrev) {
      e.preventDefault();
      handleNavigate('prev');
    } else if (e.key === 'ArrowRight' && canNavigateNext) {
      e.preventDefault();
      handleNavigate('next');
    }
  };
  
  if (!product) return null;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="edit-product-modal"
    >
      <div className="edit-product-modal__header" onKeyDown={handleKeyDown}>
        <h2>{t('productRegistration.edit.modalTitle', { 
          index: productIndex + 1, 
          total: products.length 
        })}</h2>
        <div className="edit-product-modal__nav">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleNavigate('prev')}
            disabled={!canNavigatePrev}
            leftIcon={<ArrowLeftIcon />}
          >
            {t('common.prev')}
          </Button>
          <span className="edit-product-modal__counter">
            {productIndex + 1} / {products.length}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleNavigate('next')}
            disabled={!canNavigateNext}
            rightIcon={<ArrowRightIcon />}
          >
            {t('common.next')}
          </Button>
        </div>
      </div>
      
      <div className="edit-product-modal__content">
        <div className="edit-product-modal__form">
          <Input
            label={t('productRegistration.form.titleLabel')}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder={t('productRegistration.edit.titlePlaceholder')}
            required
            autoFocus
            onKeyDown={handleKeyDown}
          />
          
          <Input
            label={t('productRegistration.form.linkLabel')}
            value={editLink}
            onChange={(e) => setEditLink(e.target.value)}
            placeholder={t('productRegistration.edit.linkPlaceholder')}
            type="url"
            helpText={t('productRegistration.form.linkHelp')}
          />
          
          <div className="input-wrapper">
            <label htmlFor="edit-comment" className="input-label">
              {t('productRegistration.form.detailLabel')}
            </label>
            <textarea
              id="edit-comment"
              className="input textarea"
              value={editComment}
              onChange={(e) => setEditComment(e.target.value)}
              placeholder={t('productRegistration.edit.detailPlaceholder')}
              rows={6}
            />
            <p className="input-help">{t('productRegistration.form.detailHelp')}</p>
          </div>
          
          {securityModeEnabled && (
            <div className="security-notice">
              <span className="security-badge">🔒 {t('productRegistration.list.encryptedBadge')}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="edit-product-modal__footer">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!editTitle.trim()}
          leftIcon={<CheckIcon />}
        >
          {t('productRegistration.edit.save')}
        </Button>
        <Button
          variant="ghost"
          onClick={onClose}
          leftIcon={<XIcon />}
        >
          {t('productRegistration.edit.cancel')}
        </Button>
      </div>
    </Modal>
  );
};